import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import socket from "../../socket";
import { apiFetch } from "../../utils/api";
import { useDarkMode } from "../../hooks/useDarkMode";
const normTicketId = v => v ? String(v).split("-")[0] : null;
const stableKeyFrom = payload => (payload.order_id != null ? String(payload.order_id) : null) || normTicketId(payload.ticket_instance_id);
const findMatchingKey = (ordersObj, payload) => {
  const rootTid = normTicketId(payload.ticket_instance_id);
  const oid = payload.order_id != null ? String(payload.order_id) : null;
  const preferred = stableKeyFrom(payload);
  if (preferred && ordersObj[preferred]) return preferred;
  for (const [k, o] of Object.entries(ordersObj)) {
    const oRootTid = normTicketId(o.ticket_instance_id);
    const oOid = o.order_id != null ? String(o.order_id) : null;
    if (rootTid && oRootTid && rootTid === oRootTid || oid && oOid && oid === oOid) {
      return k;
    }
  }
  return preferred;
};
const toJson = async res => res && typeof res.json === "function" ? res.json() : res ?? {};
const isVoidedOrder = order => String(order?.status || "").toLowerCase() === "voided";
const debugReadyOrder = (label, order) => ({
  label,
  order_id: order?.order_id ?? null,
  ticket_instance_id: order?.ticket_instance_id ?? null,
  status: order?.status ?? null,
  receivedAt: order?.receivedAt ?? null,
  firedAt: order?.firedAt ?? null,
  lastBumpAt: order?.lastBumpAt ?? null,
  completedAt: order?.completedAt ?? null,
  voidedAt: order?.voidedAt ?? null
});
function isExpoBump(payload = {}) {
  if (payload.station_is_expo || payload.is_expo) return true;
  const rawRole = payload.station_type ?? payload.station_role ?? payload.sender_role ?? "";
  const role = String(rawRole).toLowerCase();
  const name = String(payload.station_name || "").toLowerCase();
  if (role.includes("expo")) return true;
  if (name.includes("expo")) return true;
  const sid = payload.station_id ?? payload.sender_station_id;
  if (sid === 1 || sid === "1") return true;
  return false;
}
export default function ReadyScreen() {
  const {
    sublocationId
  } = useParams();
  const [orders, setOrders] = useState({});
  const {
    isDark,
    toggleDarkMode
  } = useDarkMode("readyScreenDarkMode");
  const [retentionSec, setRetentionSec] = useState(600);
  const [visibleColumns, setVisibleColumns] = useState({
    received: true,
    inProgress: true,
    completed: true
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const settingsQuery = useMemo(() => {
    const params = new URLSearchParams();
    if (sublocationId) params.set("sublocation_id", String(sublocationId));
    const query = params.toString();
    return query ? `?${query}` : "";
  }, [sublocationId]);
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch(`/settings/ready_screen${settingsQuery}`);
        const data = await toJson(res);
        if (typeof data?.isDark === "boolean" && data.isDark !== !!isDark) {
          toggleDarkMode();
        }
        if (typeof data?.completedRetentionSec === "number") {
          setRetentionSec(Math.max(10, Math.round(data.completedRetentionSec)));
        }
        setVisibleColumns({
          received: data?.showReceived !== false,
          inProgress: data?.showInProgress !== false,
          completed: data?.showCompleted !== false
        });
      } catch (e) {
        console.error("Failed to load ReadyScreen settings:", e);
      } finally {
        setSettingsLoaded(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQuery]);
  const upsertOrder = (incoming, extra) => {
    setOrders(prev => {
      const parsed = typeof incoming === "string" ? JSON.parse(incoming) : incoming || {};
      const key = findMatchingKey(prev, parsed);
      if (!key) {
        return prev;
      }
      const prevRow = prev[key] || {};
      const merged = {
        ...prevRow,
        ...parsed,
        order_id: parsed.order_id ?? prevRow.order_id ?? (key && /^\d+$/.test(key) ? key : null),
        ticket_instance_id: parsed.ticket_instance_id ?? prevRow.ticket_instance_id ?? null,
        ...extra
      };
      return {
        ...prev,
        [key]: merged
      };
    });
  };
  useEffect(() => {
    if (!sublocationId) return;
    let active = true;
    (async () => {
      try {
        const res = await apiFetch(`/orders/ready-screen?sublocation_id=${encodeURIComponent(sublocationId)}`);
        const data = await toJson(res);
        if (!active || !Array.isArray(data)) return;
        setOrders(prev => {
          const next = {
            ...prev
          };
          for (const row of data) {
            if (isVoidedOrder(row)) {
              continue;
            }
            const key = stableKeyFrom(row);
            if (!key) {
              continue;
            }
            next[key] = {
              ...(next[key] || {}),
              ...row
            };
          }
          return next;
        });
      } catch (e) {
        console.error("Failed to hydrate ReadyScreen orders:", e);
      }
    })();
    return () => {
      active = false;
    };
  }, [sublocationId]);
  useEffect(() => {
    if (!sublocationId) return undefined;
    const payload = {
      sublocationId: Number(sublocationId)
    };
    const joinRoom = () => socket.emit("ready-screen:join", payload);
    joinRoom();
    socket.on("connect", joinRoom);
    return () => {
      socket.off("connect", joinRoom);
      socket.emit("ready-screen:leave", payload);
    };
  }, [sublocationId]);
  useEffect(() => {
    const onOrderReceived = d => !isVoidedOrder(d) ? upsertOrder(d, {
      status: "received",
      receivedAt: new Date().toISOString()
    }) : null;
    const onFireCourse = d => upsertOrder(d, {
      status: "inProgress",
      firedAt: new Date().toISOString()
    });
    const onBumpItem = d => upsertOrder(d, {
      status: "inProgress",
      lastBumpAt: new Date().toISOString()
    });
    const onBumpTicket = d => {
      if (isExpoBump(d)) {
        upsertOrder(d, {
          status: "completed",
          completedAt: new Date().toISOString()
        });
      } else {
        upsertOrder(d, {
          status: "inProgress",
          lastBumpAt: new Date().toISOString()
        });
      }
    };
    const onVoidTicket = d => {
      setOrders(prev => {
        const key = findMatchingKey(prev, d);
        if (!key) {
          return prev;
        }
        if (!prev[key]) return prev;
        const next = {
          ...prev
        };
        delete next[key];
        return next;
      });
    };
    socket.on("ready-screen:order-received", onOrderReceived);
    socket.on("ready-screen:fire-course", onFireCourse);
    socket.on("ready-screen:bump-item", onBumpItem);
    socket.on("ready-screen:bump-ticket", onBumpTicket);
    socket.on("ready-screen:void-ticket", onVoidTicket);
    return () => {
      socket.off("ready-screen:order-received", onOrderReceived);
      socket.off("ready-screen:fire-course", onFireCourse);
      socket.off("ready-screen:bump-item", onBumpItem);
      socket.off("ready-screen:bump-ticket", onBumpTicket);
      socket.off("ready-screen:void-ticket", onVoidTicket);
    };
  }, []);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    if (!settingsLoaded) return;
    setOrders(prev => {
      const next = {
        ...prev
      };
      for (const [k, o] of Object.entries(prev)) {
        if (o.status === "completed" && o.completedAt) {
          const completedMs = new Date(o.completedAt).getTime();
          const ageSec = Math.max(0, Math.floor((Date.now() - completedMs) / 1000));
          if (ageSec >= retentionSec) {
            delete next[k];
          }
        }
      }
      return next;
    });
  }, [now, retentionSec, settingsLoaded]);
  const values = Object.values(orders);
  const columns = [{
    key: "received",
    title: "Received",
    color: "text-blue-700 font-bold dark:text-blue-400",
    empty: "No new orders",
    items: values.filter(o => o.status === "received"),
    isCompleted: false
  }, {
    key: "inProgress",
    title: "In Progress",
    color: "text-amber-700 font-bold dark:text-amber-400",
    empty: "Nothing cooking",
    items: values.filter(o => o.status === "inProgress"),
    isCompleted: false
  }, {
    key: "completed",
    title: "Completed",
    color: "text-emerald-700 font-bold dark:text-emerald-400",
    empty: "No completed orders",
    items: values.filter(o => o.status === "completed"),
    isCompleted: true
  }].filter(column => visibleColumns[column.key]);
  return <div className="min-h-screen bg-zinc-100 text-zinc-900 dark:bg-zinc-900 dark:text-zinc-100 p-6 transition-colors font-nunito">
            <style>{`
        @keyframes borderPulse {
          0%   { box-shadow: 0 0 0 0 rgba(239,68,68,0.0); border-color: rgba(239,68,68,0.3); }
          50%  { box-shadow: 0 0 0 6px rgba(239,68,68,0.15); border-color: rgba(239,68,68,0.9); }
          100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.0); border-color: rgba(239,68,68,0.3); }
        }
        .animate-border-pulse { animation: borderPulse 2s ease-in-out infinite; }
      `}</style>

            <header className="flex items-center justify-between w-full mb-8">
                <h1 className="text-6xl italic font-black tracking-tight">ReadyScreen</h1>
            </header>

            <main className="grid gap-8 w-full h-full" style={{
      gridTemplateColumns: columns.length > 0 ? `repeat(${columns.length}, minmax(0, 1fr))` : "minmax(0, 1fr)"
    }}>
                {columns.map(column => <Column key={column.key} title={column.title} color={column.color} empty={column.empty} items={column.items} retentionSec={retentionSec} now={now} pulseWindowSec={20} isCompleted={column.isCompleted} />)}

                {columns.length === 0 && <div className="flex items-center justify-center rounded-3xl border border-dashed border-zinc-300 dark:border-zinc-700 bg-white/70 dark:bg-zinc-800/70 min-h-[50vh]">
                        <div className="text-center px-6">
                            <h2 className="text-2xl font-semibold mb-2">No columns selected</h2>
                            <p className="text-zinc-600 dark:text-zinc-300">
                                Turn on at least one column in Ready Screen Settings for this sublocation.
                            </p>
                        </div>
                    </div>}
            </main>
        </div>;
}
function Column({
  title,
  color,
  empty,
  items,
  retentionSec,
  now,
  pulseWindowSec,
  isCompleted
}) {
  return <div className="flex flex-col bg-white dark:bg-zinc-800 rounded-3xl shadow-lg p-6 border border-zinc-300 dark:border-zinc-700 overflow-y-auto transition-colors h-[85vh]">
            <h2 className={`text-5xl font-semibold mb-5 text-center ${color}`}>
                {title}
            </h2>
            {items.length === 0 && <p className="text-zinc-500 dark:text-zinc-400 text-center text-2xl">
                    {empty}
                </p>}
            {items.map(o => <OrderCard key={normTicketId(o.ticket_instance_id) || o.order_id} order={o} retentionSec={retentionSec} now={now} pulseWindowSec={pulseWindowSec} isCompleted={isCompleted} />)}
        </div>;
}
function OrderCard({
  order,
  retentionSec,
  now,
  pulseWindowSec,
  isCompleted
}) {
  const shortTid = normTicketId(order.ticket_instance_id);
  const displayId = order.order_id ?? shortTid ?? null;
  const ui = order.user_info ?? order.UserInfo ?? null;
  const customerName = (ui && typeof ui === "object" ? ui.MemberName ?? ui.CustomerName ?? ui.GuestName ?? ui.Name : typeof ui === "string" ? ui : null) ?? (displayId ? `Order #${displayId}` : "-");
  let shouldPulse = false;
  if (isCompleted && order.completedAt) {
    const completedMs = new Date(order.completedAt).getTime();
    const ageSec = Math.max(0, Math.floor((now - completedMs) / 1000));
    const remaining = Math.max(0, retentionSec - ageSec);
    shouldPulse = remaining > 0 && remaining <= (pulseWindowSec ?? 20);
  }
  return <div className={["border rounded-2xl p-6 mb-6 bg-zinc-50 dark:bg-zinc-900/50 shadow-md transition-colors", shouldPulse ? "border-red-500 animate-border-pulse" : "border-zinc-200 dark:border-zinc-700"].join(" ")}>
            <div className="font-bold text-4xl break-words mb-2">
                {customerName}
            </div>

            {(order.table_name || order.destination || displayId) && <div className="text-2xl text-zinc-600 dark:text-zinc-300 mb-2">
                    {order.table_name && <span>Table: {order.table_name}</span>}
                    {order.table_name && (order.destination || displayId) ? <span> | </span> : null}
                    {order.destination && <span>{order.destination}</span>}
                    {(order.destination || order.table_name) && displayId ? <span> | </span> : null}
                    {displayId && <span>#{displayId}</span>}
                </div>}

            <div className="text-2xl text-zinc-500 dark:text-zinc-400">
                {order.status === "received" && "Waiting to fire"}
                {order.status === "inProgress" && "Cooking in progress"}
                {order.status === "completed" && "Ready for pickup"}
            </div>
        </div>;
}